// utils/filenameUtils.js
import { generateSafeFolderName, generateSafeGuideFilename, sanitizeFilename } from './filenameSanitizer';

/**
 * Utility functions for consistent filename handling across the application
 */

const stripJsonExtension = (value = '') => value.replace(/\.json$/i, '');

export const getCanonicalFilename = (metadata) => {
  if (metadata?.filename) {
    return sanitizeFilename(metadata.filename, { maxLength: Infinity, preserveHyphens: true });
  }

  return generateSafeGuideFilename(metadata || {});
};

export const getCanonicalFolderName = (metadata) => {
  if (metadata?.filename) {
    return stripJsonExtension(getCanonicalFilename(metadata));
  }

  return generateSafeFolderName(metadata || {});
};

/**
 * Extracts the base filename (without extension) from a metadata object
 * 
 * @param {Object} metadata - The itinerary metadata object
 * @return {string} The base filename without extension
 */
export const getBaseFilename = (metadata) => {
    if (metadata && (metadata.filename || (metadata.country && metadata.name))) {
      return getCanonicalFolderName(metadata);
    }

    return metadata && metadata.name ? sanitizeForFilename(metadata.name) : '';
  };
  
  /**
   * Sanitizes a string to be safe for use in filenames
   * 
   * @param {string} text - The text to sanitize
   * @return {string} The sanitized text
   */
  export const sanitizeForFilename = (text) => {
    if (!text) return '';
    
    return text
      .trim()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Remove accents/diacritics
      .replace(/[^a-zA-Z0-9]/g, '_') // Replace non-alphanumeric with underscore
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, '') // Remove leading/trailing underscores
      .toLowerCase();
  };
  
  /**
   * Formats a consistent itinerary filename based on standard pattern
   * 
   * @param {string} country - The country name
   * @param {string} language - The language code
   * @param {string} name - The itinerary name
   * @param {boolean} includeExtension - Whether to include .json extension
   * @return {string} The formatted filename
   */
  export const formatItineraryFilename = (country, language, name, includeExtension = true) => {
    // Format language properly as xx-XX if not already in that format
    let formattedLanguage = language || 'en-US';
    if (!formattedLanguage.includes('-')) {
      // Convert simple language codes to standard format
      const langMap = {
        'en': 'en-US',
        'pt': 'pt-PT',
        'es': 'es-ES',
        'fr': 'fr-FR',
        'de': 'de-DE',
        'it': 'it-IT',
        'bg': 'bg-BG'
      };
      formattedLanguage = langMap[formattedLanguage] || `${formattedLanguage}-${formattedLanguage.toUpperCase()}`;
    }
    
    const sanitizedCountry = sanitizeForFilename(country);
    const sanitizedName = sanitizeForFilename(name);
    
    const filename = `${sanitizedCountry}_${formattedLanguage}_${sanitizedName}`;
    return includeExtension ? `${filename}.json` : filename;
  };
  
  /**
   * Creates storage path components based on itinerary metadata
   * 
   * @param {Object} metadata - The itinerary metadata
   * @return {Object} Object with path components for storage operations
   */
  export const getStoragePathComponents = (metadata) => {
    const country = metadata.country || '';
    const safeCountry = country.trim().toLowerCase().replace(/[^\w-]/g, '-');

    const filename = getCanonicalFilename(metadata);
    const folderName = getCanonicalFolderName(metadata);

    return {
      country: safeCountry,
      folderName, // For the itinerary folder structure
      filename,   // For the actual JSON file
      fullPath: `${safeCountry}/${folderName}/${filename}`
    };
  };
  
  export default {
    getBaseFilename,
    getCanonicalFilename,
    getCanonicalFolderName,
    sanitizeForFilename,
    formatItineraryFilename,
    getStoragePathComponents
  };
